import { create } from 'zustand';
import { isApiError } from '@/services/api/client';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';
import {
  ClientProjectCreationResponse,
  serviceProviderApi,
} from '@/services/api/serviceProviderApi';
import type { ClientProject } from '@/types';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (isApiError(error)) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const requireResponseData = <T,>(
  data: T | undefined,
  fallbackMessage: string,
): T => {
  if (data === undefined) {
    throw new Error(fallbackMessage);
  }

  return data;
};

interface ClientProjectState {
  clientProjects: ClientProject[];
  currentClientProject: ClientProject | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  fetchClientProjects: (params?: { limit?: number; offset?: number; status?: string }) => Promise<void>;
  createClientProject: (
    data: Parameters<typeof serviceProviderApi.createClientProject>[0],
  ) => Promise<ClientProjectCreationResponse>;
  getClientProject: (id: string) => Promise<ClientProject>;
  updateClientProject: (id: string, data: Partial<ClientProject>) => Promise<ClientProject>;
  setProvisioningConfig: (id: string, config: {
    homeProjectId?: string;
    homePageId?: string;
    quickPages?: Array<{ pageId: string; label: string; icon?: string }>;
    theme?: string;
  }) => Promise<ClientProject>;
  clearError: () => void;
  reset: () => void;
}

export const useClientProjectStore = create<ClientProjectState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      clientProjects: [],
      currentClientProject: null,
      total: 0,
      isLoading: false,
      error: null,
      lastFetched: null,

      fetchClientProjects: async (params) => {
        const { lastFetched, isLoading } = get();
        if (isLoading) return;
        if (lastFetched && Date.now() - lastFetched < CACHE_DURATION && !params) return;

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.listClientProjects(params);
          const apiData = response.data ?? { clientProjects: [], total: 0 };
          set((state) => {
            state.clientProjects = apiData.clientProjects;
            state.total = apiData.total;
            state.isLoading = false;
            state.lastFetched = Date.now();
          });
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to fetch client projects');
          });
        }
      },

      createClientProject: async (data) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.createClientProject(data);
          const result = requireResponseData(
            response.data,
            'Failed to create client project',
          );
          set((state) => {
            state.clientProjects.unshift(result.clientProject);
            state.total += 1;
            state.isLoading = false;
            state.lastFetched = null; // Force refetch
          });
          return result;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to create client project');
          });
          throw error;
        }
      },

      getClientProject: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.getClientProject(id);
          const clientProject = requireResponseData(
            response.data,
            'Failed to fetch client project',
          );
          set((state) => {
            state.currentClientProject = clientProject;
            state.isLoading = false;
          });
          return clientProject;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to fetch client project');
          });
          throw error;
        }
      },

      updateClientProject: async (id, data) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.updateClientProject(id, data);
          const updated = requireResponseData(
            response.data,
            'Failed to update client project',
          );
          set((state) => {
            state.currentClientProject = updated;
            const idx = state.clientProjects.findIndex((p) => p._id === id);
            if (idx !== -1) state.clientProjects[idx] = updated;
            state.isLoading = false;
          });
          return updated;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to update client project');
          });
          throw error;
        }
      },

      setProvisioningConfig: async (id, config) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.setProvisioningConfig(id, config);
          const updated = requireResponseData(
            response.data,
            'Failed to update provisioning config',
          );
          set((state) => {
            state.currentClientProject = updated;
            const idx = state.clientProjects.findIndex((p) => p._id === id);
            if (idx !== -1) state.clientProjects[idx] = updated;
            state.isLoading = false;
          });
          return updated;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getErrorMessage(error, 'Failed to update provisioning config');
          });
          throw error;
        }
      },

      clearError: () => {
        set((state) => { state.error = null; });
      },

      reset: () => {
        set((state) => {
          state.clientProjects = [];
          state.currentClientProject = null;
          state.total = 0;
          state.isLoading = false;
          state.error = null;
          state.lastFetched = null;
        });
      },
    }))
  )
);

// Reset on logout
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useClientProjectStore.getState().reset();
        }
      }
    );
  });
}

// Selector hooks
export const useClientProjects = () =>
  useClientProjectStore(useShallow((s) => ({
    clientProjects: s.clientProjects,
    total: s.total,
    isLoading: s.isLoading,
    error: s.error,
  })));

export const useCurrentClientProject = () =>
  useClientProjectStore(useShallow((s) => ({
    clientProject: s.currentClientProject,
    isLoading: s.isLoading,
    error: s.error,
  })));

export const useClientProjectActions = () =>
  useClientProjectStore(useShallow((s) => ({
    fetchClientProjects: s.fetchClientProjects,
    createClientProject: s.createClientProject,
    getClientProject: s.getClientProject,
    updateClientProject: s.updateClientProject,
    setProvisioningConfig: s.setProvisioningConfig,
    clearError: s.clearError,
    reset: s.reset,
  })));
